import React, { FC, useCallback, useMemo } from "react";
import { ResourceTypeLink } from "@evergis/api";
import { useTranslation } from "react-i18next";

import {
  ActionsGroup,
  Description,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  RaisedButton,
  RatioLine,
} from "@evergis/ui";

import { LimitsExceededContent, RatioLinesContainer } from "./styled";

import { useCheckLimits } from "../../../hooks/useCheckLimits";
import { useResourceType } from "../../../hooks/useResourceType";
import { getRatio } from "../../../utils/math";
import { useDialogReset } from "../../../hooks/useDialogs";

type RatioLineVariant = Partial<Record<"primary" | "secondary" | "error", boolean>>;

const WARN_ENTITY_COUNT_THRESHOLD = 5;

export const LimitsExceeded: FC = () => {
  const { t } = useTranslation();
  const [resourceType] = useResourceType();
  const resetDialog = useDialogReset();
  const { limits } = useCheckLimits();
  const projectsCount: number = useMemo(() => (limits ? limits.currentProjectCount : 0), [limits]);
  const layersCount: number = useMemo(() => (limits ? limits.currentServiceCount : 0), [limits]);
  const tablesCount: number = useMemo(() => (limits ? limits.currentTableCount : 0), [limits]);
  const maxProjectsCount: number = useMemo(() => (limits ? limits.maxProjectsCount : 0), [limits]);
  const maxLayersCount: number = useMemo(() => (limits ? limits.maxServicesCount : 0), [limits]);
  const maxTablesCount: number = useMemo(() => (limits ? limits.maxTablesCount : 0), [limits]);
  const isProjectsLimit: boolean = maxProjectsCount >= 0 && projectsCount >= maxProjectsCount;
  const isLayersLimit: boolean = maxLayersCount >= 0 && layersCount >= maxLayersCount;
  const isTablesLimit: boolean = maxTablesCount >= 0 && tablesCount >= maxTablesCount;
  const projectsRatio: number = getRatio(projectsCount, maxProjectsCount) || 0;
  const layersRatio: number = getRatio(layersCount, maxLayersCount) || 0;
  const tablesRatio: number = getRatio(tablesCount, maxTablesCount) || 0;

  const getDialogTitle = useCallback((): string => {
    if (resourceType === ResourceTypeLink.Project && isProjectsLimit) {
      return t("limitsExceeded.dialogHeader1", { ns: "dialogs" });
    }

    if (!isLayersLimit && isTablesLimit) {
      return t("limitsExceeded.dialogHeader2", { ns: "dialogs" });
    }

    if (isLayersLimit && isTablesLimit) {
      return resourceType === ResourceTypeLink.Table
        ? t("limitsExceeded.dialogHeader2", { ns: "dialogs" })
        : t("limitsExceeded.dialogHeader3", { ns: "dialogs" });
    }

    return t("limitsExceeded.dialogHeader3", { ns: "dialogs" });
  }, [resourceType, isProjectsLimit, isLayersLimit, isTablesLimit, t]);

  const getDescription = useCallback((): string => {
    if (resourceType === ResourceTypeLink.Project && isProjectsLimit) {
      return t("limitsExceeded.description1", { ns: "dialogs" });
    }

    if (!isLayersLimit && isTablesLimit) {
      return t("limitsExceeded.description2", { ns: "dialogs" });
    }

    if (isLayersLimit && isTablesLimit) {
      return t("limitsExceeded.description3", { ns: "dialogs" });
    }

    return t("limitsExceeded.description4", { ns: "dialogs" });
  }, [resourceType, isProjectsLimit, isLayersLimit, isTablesLimit, t]);

  return (
    <Dialog isOpen maxWidth={"39rem"} onCloseRequest={resetDialog}>
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <DialogContent>
        <LimitsExceededContent>
          <Description>
            {getDescription()}{" "}
            <Link href="mailto:info@everpoint.ru" style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
              {t("limitsExceeded.mailUs", { ns: "dialogs" })}
            </Link>
            , {t("limitsExceeded.weWillHelp", { ns: "dialogs" })}
          </Description>
          <RatioLinesContainer>
            <RatioLine
              ratio={projectsRatio}
              {...getRatioLineVariant(projectsCount, maxProjectsCount)}
              title={t("limitsExceeded.maps", { ns: "dialogs" })}
              details={`${projectsCount} ${t("limitsExceeded.of", { ns: "dialogs" })} ${
                maxProjectsCount < 0 ? "∞" : maxProjectsCount
              }`}
            />
            <RatioLine
              ratio={layersRatio}
              {...getRatioLineVariant(layersCount, maxLayersCount)}
              title={t("limitsExceeded.layers", { ns: "dialogs" })}
              details={`${layersCount} ${t("limitsExceeded.of", { ns: "dialogs" })} ${
                maxLayersCount < 0 ? "∞" : maxLayersCount
              }`}
            />
            <RatioLine
              ratio={tablesRatio}
              {...getRatioLineVariant(tablesCount, maxTablesCount)}
              title={t("limitsExceeded.tables", { ns: "dialogs" })}
              details={`${tablesCount} ${t("limitsExceeded.of", { ns: "dialogs" })} ${
                maxTablesCount < 0 ? "∞" : maxTablesCount
              }`}
            />
          </RatioLinesContainer>
        </LimitsExceededContent>
      </DialogContent>
      <DialogActions>
        <ActionsGroup>
          <RaisedButton onClick={resetDialog} primary>
            {t("actions.ok", { ns: "common" })}
          </RaisedButton>
        </ActionsGroup>
      </DialogActions>
    </Dialog>
  );
};

function getRatioLineVariant(curr: number, total: number): RatioLineVariant {
  if (curr >= total) {
    return { error: true };
  }

  if (total - curr <= WARN_ENTITY_COUNT_THRESHOLD) {
    return { secondary: true };
  }

  return { primary: true };
}
